import ScrollReveal from "@/components/base/ScrollReveal";

interface Article {
  title: string;
  summary: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  content: string;
}

interface FeaturedArticlesProps {
  articles: Article[];
  onOpenArticle: (article: Article) => void;
}

export default function FeaturedArticles({
  articles,
  onOpenArticle,
}: FeaturedArticlesProps) {
  const featured = articles.slice(0, 3);

  return (
    <section className="py-16 md:py-24 bg-brand-card border-y border-brand-border">
      <div className="container-main">
        <ScrollReveal className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 border border-brand-border rounded-full px-5 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-brand-gold" />
            <span className="text-brand-gold text-[0.9375rem] font-medium tracking-wide">
              精選文章
            </span>
          </div>
          <h2 className="text-white font-display font-bold text-[1.75rem] md:text-[2.5rem] leading-[1.3] mb-4">
            編輯精選｜必讀深度觀點
          </h2>
          <p className="text-brand-text-secondary text-[1.125rem] md:text-[1.25rem] leading-[1.7] max-w-[640px] mx-auto">
            從保單健診到退休傳承，這幾篇文章幫你建立完整的財務思維框架。
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Left large card */}
          <ScrollReveal className="md:col-span-2 md:row-span-2" delay={0}>
            <div
              className="group relative h-full min-h-[380px] md:min-h-[520px] rounded-2xl overflow-hidden cursor-pointer border border-brand-border hover:border-brand-gold/50 transition-all duration-300"
              onClick={() => onOpenArticle(featured[0])}
            >
              <img
                src={featured[0].image}
                alt={featured[0].title}
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                <span className="inline-block self-start border border-brand-gold/50 rounded-full px-3 py-1 text-brand-gold text-[0.875rem] font-medium mb-4">
                  {featured[0].category}
                </span>
                <h3 className="text-white font-display font-bold text-[1.5rem] md:text-[2rem] leading-[1.35] mb-3 group-hover:text-brand-gold transition-colors">
                  {featured[0].title}
                </h3>
                <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75] mb-4 max-w-[560px]">
                  {featured[0].summary}
                </p>
                <div className="flex items-center gap-3 text-brand-text-secondary text-[0.9375rem]">
                  <span>{featured[0].date}</span>
                  <span className="w-1 h-1 rounded-full bg-brand-border" />
                  <span>{featured[0].readTime}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Right stacked cards */}
          {featured.slice(1, 3).map((article, idx) => (
            <ScrollReveal key={article.title} delay={100 + idx * 100}>
              <div
                className="group bg-brand-black border border-brand-border rounded-2xl overflow-hidden transition-all duration-300 hover:border-brand-gold/50 cursor-pointer h-full flex flex-col"
                onClick={() => onOpenArticle(article)}
              >
                <div className="relative w-full h-[160px] md:h-[180px] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-transparent to-transparent" />
                </div>
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <span className="inline-block self-start border border-brand-border rounded-full px-3 py-1 text-brand-gold text-[0.8125rem] font-medium mb-3">
                    {article.category}
                  </span>
                  <h3 className="text-white font-semibold text-[1.125rem] md:text-[1.375rem] leading-[1.45] mb-2 group-hover:text-brand-gold transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-brand-text-secondary text-[1rem] leading-[1.65] flex-1">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-3 text-brand-text-secondary/70 text-[0.875rem] mt-4 pt-4 border-t border-brand-border">
                    <span>{article.date}</span>
                    <span className="w-1 h-1 rounded-full bg-brand-border" />
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}